<script setup lang="ts">
import { usePage } from '@inertiajs/vue3';
import { MessageCircle, Plus } from '@lucide/vue';
import { computed, ref } from 'vue';

import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

import { useCommunicationTranslations } from '../composables/useCommunicationTranslations';
import { type InboxItem, useConversations } from '../composables/useConversations';
import { useMessenger } from '../composables/useMessenger';
import { useUnreadCount } from '../composables/useUnreadCount';

import ConversationList from './ConversationList.vue';
import NewConversationDialog from './NewConversationDialog.vue';

const { conversations, isLoading, refresh } = useConversations();
const { tm } = useCommunicationTranslations('communication');
const messenger = useMessenger();

const page = usePage();
const userId = computed(() => {
    const auth = (page.props as Record<string, unknown>).auth as { user?: { id: string } } | undefined
    return auth?.user?.id
})
const { totalUnread } = useUnreadCount(userId);

const isOpen = ref(false);
const isNewDialogOpen = ref(false);

function handleOpenChange(open: boolean) {
    isOpen.value = open;
    if (open) {
        refresh();
    }
}

function handleViewMemo(memo: InboxItem) {
    isOpen.value = false;
    void messenger.viewCommunication(memo.communication_id);
}
</script>

<template>
    <DropdownMenu :open="isOpen" @update:open="handleOpenChange">
        <DropdownMenuTrigger as-child>
            <Button variant="ghost" size="icon" class="relative">
                <MessageCircle class="size-5" />
                <div
                    v-if="totalUnread > 0"
                    class="absolute -top-1 -right-1 flex size-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground"
                >
                    {{ totalUnread > 9 ? '9+' : totalUnread }}
                </div>
            </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" class="w-80 p-0">
            <div class="flex items-center justify-between border-b p-3">
                <span class="font-semibold">{{ tm('messages.conversations') }}</span>
                <Button
                    variant="ghost"
                    size="icon"
                    class="h-8 w-8"
                    @click="isNewDialogOpen = true"
                >
                    <Plus class="size-4" />
                </Button>
            </div>
            <ConversationList
                :conversations="conversations"
                :is-loading="isLoading"
                @view-memo="handleViewMemo"
                @selected="isOpen = false"
            />
            <div class="border-t p-2 text-center">
                <a href="/inbox" class="text-xs text-primary hover:underline">
                    {{ tm('inbox.view_all') }}
                </a>
            </div>
        </DropdownMenuContent>
    </DropdownMenu>

    <NewConversationDialog
        v-model:open="isNewDialogOpen"
    />

</template>
